import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone'
import { toast } from 'sonner'
import {
  ACCEPTED_MIME,
  AUDIO_MIME,
  DOCUMENTS_MIME,
  IMAGES_MIME,
  MAX_FILE_SIZE,
  MAX_FILES,
  VIDEOS_MIME,
} from './constants'
import type { AttachmentPreview } from './types'
import { createAttachmentPreview, revokeAttachmentPreview } from './utils'

type UseAttachmentsProps = {
  hasTicket: boolean
}

export function useAttachments({ hasTicket }: UseAttachmentsProps) {
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([])
  const attachmentsRef = useRef<AttachmentPreview[]>([])

  useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach(revokeAttachmentPreview)
    }
  }, [])

  const handleDropAccepted = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return

    setAttachments((prev) => {
      const availableSlots = MAX_FILES - prev.length
      if (availableSlots <= 0) {
        toast.error(`Você pode enviar até ${MAX_FILES} arquivos por mensagem.`)
        return prev
      }

      const filesToUse = acceptedFiles.slice(0, availableSlots)
      const mapped = filesToUse.map(createAttachmentPreview)
      return [...prev, ...mapped]
    })
  }, [])

  const handleDropRejected = useCallback((fileRejections: FileRejection[]) => {
    fileRejections.forEach((rejection) => {
      const reason = rejection.errors.map((error) => error.message).join(', ')
      toast.error(`${rejection.file.name} não foi aceito: ${reason}`)
    })
  }, [])

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const removed = prev.find((item) => item.id === id)
      if (removed) {
        revokeAttachmentPreview(removed)
      }
      return prev.filter((item) => item.id !== id)
    })
  }, [])

  const clearAttachments = useCallback(() => {
    setAttachments([])
  }, [])

  const revokeAttachmentPreviews = useCallback((items: AttachmentPreview[]) => {
    items.forEach(revokeAttachmentPreview)
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_MIME,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    noClick: true,
    multiple: true,
    disabled: !hasTicket,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  })

  const openFilePickerByType = useCallback(
    (acceptMime: Accept) => {
      if (!hasTicket) return

      const input = document.createElement('input')
      input.type = 'file'
      input.multiple = true
      input.accept = Object.entries(acceptMime)
        .flatMap(([_mime, extensions]) => extensions)
        .join(',')

      input.onchange = (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || [])
        if (files.length === 0) return

        const validFiles: File[] = []
        const rejectedFiles: FileRejection[] = []

        files.forEach((file) => {
          if (file.size > MAX_FILE_SIZE) {
            rejectedFiles.push({
              file,
              errors: [
                {
                  code: 'file-too-large',
                  message: `Arquivo muito grande (máximo ${MAX_FILE_SIZE / 1024 / 1024}MB)`,
                },
              ],
            })
            return
          }

          const isAccepted = Object.entries(acceptMime).some(
            ([mimePattern, extensions]) => {
              const regex = new RegExp(mimePattern.replace('*', '.*'))
              const matchesMime = regex.test(file.type)
              const matchesExt = extensions.some((ext) =>
                file.name.toLowerCase().endsWith(ext)
              )
              return matchesMime || matchesExt
            }
          )

          if (isAccepted) {
            validFiles.push(file)
          } else {
            rejectedFiles.push({
              file,
              errors: [
                {
                  code: 'file-invalid-type',
                  message: 'Tipo de arquivo inválido',
                },
              ],
            })
          }
        })

        if (validFiles.length > 0) {
          handleDropAccepted(validFiles)
        }
        if (rejectedFiles.length > 0) {
          handleDropRejected(rejectedFiles)
        }
      }

      input.click()
    },
    [hasTicket, handleDropAccepted, handleDropRejected]
  )

  const openImagePicker = useCallback(
    () => openFilePickerByType(IMAGES_MIME),
    [openFilePickerByType]
  )
  const openVideoPicker = useCallback(
    () => openFilePickerByType(VIDEOS_MIME),
    [openFilePickerByType]
  )
  const openDocumentPicker = useCallback(
    () => openFilePickerByType(DOCUMENTS_MIME),
    [openFilePickerByType]
  )
  const openAudioPicker = useCallback(
    () => openFilePickerByType(AUDIO_MIME),
    [openFilePickerByType]
  )

  return {
    attachments,
    getRootProps,
    getInputProps,
    isDragActive,
    openFilePicker: open,
    openImagePicker,
    openVideoPicker,
    openDocumentPicker,
    openAudioPicker,
    handleRemoveAttachment,
    clearAttachments,
    revokeAttachmentPreviews,
  }
}
